import React from 'react';
import { createPortal } from 'react-dom';
import { TimelineFileUploadOptions } from '../interfaces/Frameworks';
import { Button } from './Button';
import { getTimelineFileError, getTimelineMediaError, TIMELINE_MEDIA_ACCEPT, getTimelineUploadError, isTimelineUploadCancelled, TIMELINE_FILE_ACCEPT } from '../timelineFileUpload';
import './TimelineUploadDialog.css';

type Props = {
    clientName: string;
    kind?: 'document' | 'media';
    onUpload: (file: File, options: TimelineFileUploadOptions) => Promise<string>;
    onUploaded: (documentId: string, fileName: string) => void;
    onClose: () => void;
};

// Keep file selection, cancellation and retry local to one client's upload dialog.
export function TimelineUploadDialog({ clientName, kind = 'document', onUpload, onUploaded, onClose }: Props) {
    const [file, setFile] = React.useState<File | null>(null);
    const [error, setError] = React.useState('');
    const [uploadFailed, setUploadFailed] = React.useState(false);
    const [uploading, setUploading] = React.useState(false);
    const [cancelling, setCancelling] = React.useState(false);
    const [confirmClose, setConfirmClose] = React.useState(false);
    const [progress, setProgress] = React.useState(0);
    const [dragging, setDragging] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const controllerRef = React.useRef<AbortController | null>(null);
    const mountedRef = React.useRef(true);
    const dragDepthRef = React.useRef(0);
    const titleId = React.useId();
    const isMedia = kind === 'media';

    const requestClose = React.useCallback(() => {
        if (controllerRef.current) {
            setConfirmClose(true);
        } else {
            onClose();
        }
    }, [onClose]);

    React.useEffect(() => {
        // Prevent files dropped outside the target from navigating away from the dashboard.
        mountedRef.current = true;
        const previousFocus = document.activeElement as HTMLElement | null;
        dialogRef.current?.querySelector<HTMLButtonElement>('.timeline-upload-choose')?.focus();
        const preventFileNavigation = (event: DragEvent) => {
            if (Array.from(event.dataTransfer?.types || []).includes('Files')) event.preventDefault();
        };
        window.addEventListener('dragover', preventFileNavigation);
        window.addEventListener('drop', preventFileNavigation);
        return () => {
            mountedRef.current = false;
            controllerRef.current?.abort();
            window.removeEventListener('dragover', preventFileNavigation);
            window.removeEventListener('drop', preventFileNavigation);
            previousFocus?.focus();
        };
    }, []);

    // Both entry points validate before a file can be submitted.
    const selectFiles = (files: File[]) => {
        if (controllerRef.current) return;
        if (files.length === 0) return;
        setUploadFailed(false);
        const selectionError = files.length !== 1 ? 'Please choose one file at a time.' : (isMedia ? getTimelineMediaError(files[0]) : getTimelineFileError(files[0]));
        setError(selectionError);
        setFile(selectionError ? null : files[0]);
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        dragDepthRef.current = 0;
        setDragging(false);
        if (controllerRef.current) return;
        const items = Array.from(event.dataTransfer.items || []);
        if (items.some((item) => item.webkitGetAsEntry?.()?.isDirectory)) {
            setUploadFailed(false);
            setFile(null);
            setError('Folders cannot be uploaded. Choose one file.');
            return;
        }
        selectFiles(Array.from(event.dataTransfer.files));
    };

    const upload = async () => {
        if (!file || controllerRef.current) return;
        const controller = new AbortController();
        controllerRef.current = controller;
        setUploading(true);
        setProgress(0);
        setError('');
        setUploadFailed(false);
        try {
            const documentId = await onUpload(file, {
                signal: controller.signal,
                onProgress: (value) => {
                    if (mountedRef.current) setProgress(value);
                },
            });
            // A completed save wins a last-moment cancellation request.
            if (mountedRef.current) onUploaded(documentId, file.name);
        } catch (uploadError) {
            if (!mountedRef.current) return;
            if (isTimelineUploadCancelled(uploadError)) {
                onClose();
            } else {
                setError(getTimelineUploadError(uploadError));
                setUploadFailed(true);
                setConfirmClose(false);
            }
        } finally {
            controllerRef.current = null;
            if (mountedRef.current) {
                setUploading(false);
                setCancelling(false);
            }
        }
    };

    const cancelUpload = () => {
        setCancelling(true);
        setConfirmClose(false);
        controllerRef.current?.abort();
    };

    // Keep normal dialog navigation local and route every close request through cancellation.
    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            event.stopPropagation();
            if (cancelling) return;
            if (confirmClose) setConfirmClose(false);
            else requestClose();
        }
        if (event.key === 'Tab') {
            const controls = dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not([hidden]):not(:disabled)');
            if (!controls?.length) return;
            const first = controls[0];
            const last = controls[controls.length - 1];
            if (event.shiftKey && document.activeElement === first) {
                event.preventDefault(); last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault(); first.focus();
            }
        }
    };

    return createPortal(
        <div className='timeline-upload-backdrop' onClick={(event) => {
            if (event.target === event.currentTarget && !cancelling) requestClose();
        }}>
            <div ref={dialogRef} className='timeline-upload-dialog' role='dialog' aria-modal='true' aria-labelledby={titleId} onKeyDown={handleKeyDown}>
                <button className='timeline-upload-close' type='button' aria-label='Close upload dialog' onClick={requestClose} disabled={cancelling}>
                    <svg width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='3' aria-hidden='true' focusable='false'>
                        <path d='M5 5L19 19M19 5L5 19' />
                    </svg>
                </button>
                <h2 id={titleId}>{isMedia ? 'Import audio/video' : 'Upload file'}</h2>
                <p className='timeline-upload-destination'>{isMedia ? 'Add a recording to ' : 'Add a file to '}<strong>{clientName}</strong>’s timeline{isMedia && <><br />We’ll process the recording and create a transcript.</>}</p>
                <input ref={inputRef} type='file' accept={isMedia ? TIMELINE_MEDIA_ACCEPT : TIMELINE_FILE_ACCEPT} hidden aria-label='Choose timeline file' onChange={(event) => {
                    selectFiles(Array.from(event.currentTarget.files || []));
                    event.currentTarget.value = '';
                }} />
                <div
                    role='group' aria-label='File drop area'
                    className={'timeline-upload-dropzone' + (dragging ? ' is-dragging' : '') + (uploading ? ' is-busy' : '')}
                    onDragEnter={(event) => {
                        if (!Array.from(event.dataTransfer.types).includes('Files')) return;
                        event.preventDefault();
                        dragDepthRef.current += 1;
                        if (!controllerRef.current) setDragging(true);
                    }}
                    onDragOver={(event) => {
                        event.preventDefault();
                        event.dataTransfer.dropEffect = uploading ? 'none' : 'copy';
                    }}
                    onDragLeave={(event) => {
                        event.preventDefault();
                        dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
                        if (dragDepthRef.current === 0) setDragging(false);
                    }}
                    onDrop={handleDrop}
                >
                    <div className='timeline-upload-icon' aria-hidden='true'>
                        <img src={isMedia ? require('./assets/icons/media_import_normal.svg').default : require('./assets/icons/file_upload_normal.svg').default} alt='' />
                    </div>
                    <p className='timeline-upload-prompt'>{dragging ? 'Drop your file here' : 'Drag and drop a file here'}</p>
                    <p className='timeline-upload-limit'>One file at a time</p>
                    <span className='timeline-upload-or'>or</span>
                    <button className='timeline-upload-choose' type='button' disabled={uploading} onClick={() => inputRef.current?.click()}>
                        {file ? 'Choose another file' : 'Choose file'}
                    </button>
                    <p className='timeline-upload-hint'>{isMedia ? 'Audio or video, including MP3, M4A, WAV, MP4 and MOV' : 'PDF, Word, images or text'}</p>
                </div>
                {file && <div className='timeline-upload-file'>
                    <span className='timeline-upload-filename'>{file.name}</span>
                    <span className='timeline-upload-size'>{file.size < 1024 * 1024 ? Math.max(1, Math.ceil(file.size / 1024)) + ' KB' : (file.size / (1024 * 1024)).toFixed(1) + ' MB'}</span>
                </div>}
                {uploading && <div className='timeline-upload-progress' role='status'>
                    <span>{cancelling ? 'Cancelling upload…' : progress === 100 ? 'Adding to timeline…' : 'Uploading… ' + progress + '%'}</span>
                    <progress value={progress < 100 && !cancelling ? progress : undefined} max={100} aria-label={progress < 100 ? 'Upload progress' : 'Adding to timeline'} />
                </div>}
                {!!error && <p className='timeline-upload-error' role='alert'>{error}</p>}
                {confirmClose ? <div className='timeline-upload-confirm' role='alert'>
                    <strong>Cancel this upload?</strong>
                    <p>Closing will cancel the upload.</p>
                    <div className='timeline-upload-actions'>
                        <Button label='Keep uploading' type='secondary' onClick={() => setConfirmClose(false)} />
                        <Button label='Cancel upload' type='primary' onClick={cancelUpload} />
                    </div>
                </div> : <div className='timeline-upload-actions'>
                    <Button label='Close' type='secondary' onClick={requestClose} disabled={cancelling} />
                    <Button label={uploading ? 'Uploading…' : uploadFailed ? 'Retry' : isMedia ? 'Import' : 'Upload'} type='primary' onClick={() => void upload()} disabled={!file || uploading} />
                </div>}
            </div>
        </div>, document.body
    );
}
